import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ProjectSchedule.css";
import {
  ProjectScheduleProps,
  ClientProject,
  Month,
  WorkCategory,
  WORK_CATEGORY_LABELS,
  Status,
  STATUS_LABELS,
} from "../types";
import { Project } from "../models/Project";
import { Category } from "../models/Category";
import { exportSchedule } from "../utils/csvExport";
import {
  hasWarnings,
  getPrimaryWarning,
  getWarningTooltip,
} from "../utils/projectWarnings";
import {
  formatDateFrench,
  parseDateFromFrench,
  parseDateToFrench,
} from "../utils/dateFormatting";
import {
  getMonthlyCapacity,
  getTotalMonthlyCapacity,
  isOverCapacity,
  getCapacityUtilization,
  getConfiguredMonthlyCapacity,
  setConfiguredMonthlyCapacity,
  resetConfiguredMonthlyCapacity,
  MonthlyCapacity,
} from "../config/availableEffort";
import AdminPanel from "./AdminPanel";
import ClientProjectModal from "./ClientProjectModal";
import { CommentsModal } from "./CommentsModal";
import { useAuth } from "../context/AuthContext";
import { listConnectedUsers, updateUserRole } from "../services/authService";
import { ManagedUser, UserRole } from "../types/auth";

const ProjectSchedule: React.FC<ProjectScheduleProps> = ({
  schedule,
  clientProjects,
  onCreateClientProject,
  onCategoryClientProjectLabelChange,
  onProjectClick,
  onAddProject,
  onAddTopLevelCategory,
  onCategoryColorChange,
  onCategoryNameChange,
  onDeleteCategory,
  onReorderCategory,
  onImportCSV,
  onProjectStatusChange,
  onOpenBackup,
  onConvertCategoryToProject,
  onConvertProjectToCategory,
  onMoveCategory,
  onMoveProject,
  onShiftCategoryTiming,
  onCapacityConfigChange,
}) => {
  const ALL_CLIENT_PROJECTS = "__all__";
  const {
    user: authUser,
    setUser: setAuthUser,
    canEdit,
    canViewWorkload,
    canManageUsers,
    canCreateComments,
    canViewComments,
  } = useAuth();
  const allWorkCategories = Object.values(WorkCategory);
  const allStatuses = Object.values(Status);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const headerRowRef = useRef<HTMLDivElement>(null);
  const totalRowRef = useRef<HTMLDivElement>(null);
  const breakdownRowRef = useRef<HTMLDivElement>(null);
  const workCategoryFilterRef = useRef<HTMLDivElement>(null);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const clientProjectFilterRef = useRef<HTMLDivElement>(null);

  // Track collapsed state for each category
  const [collapsedCategories, setCollapsedCategories] = useState<Set<number>>(
    new Set(),
  );

  // Track collapsed state for total row
  const [isTotalCollapsed, setIsTotalCollapsed] = useState<boolean>(false);

  // Track selected work category filter
  const [selectedWorkCategories, setSelectedWorkCategories] = useState<
    Set<WorkCategory>
  >(() => new Set(Object.values(WorkCategory)));

  // Track hidden statuses (statuses to filter out)
  // Active statuses (Planned, In Progress) cannot be filtered
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<Status>>(new Set());

  // Track custom date range overrides
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [customStartDateDisplay, setCustomStartDateDisplay] =
    useState<string>("");
  const [customEndDateDisplay, setCustomEndDateDisplay] = useState<string>("");

  // Toggle for totals display mode
  const [totalDisplayMode, setTotalDisplayMode] = useState<
    "aggregated" | "remaining"
  >("remaining");
  const [isWorkCategoryFilterOpen, setIsWorkCategoryFilterOpen] =
    useState<boolean>(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState<boolean>(false);
  const [isClientProjectFilterOpen, setIsClientProjectFilterOpen] =
    useState<boolean>(false);
  const [clientProjectSearch, setClientProjectSearch] =
    useState<string>("");
  const [selectedClientProjectId, setSelectedClientProjectId] = useState<string>(
    ALL_CLIENT_PROJECTS,
  );
  const [isClientProjectModalOpen, setIsClientProjectModalOpen] =
    useState<boolean>(false);

  const [shiftCategoryId, setShiftCategoryId] = useState<number | null>(null);
  const [shiftStartDateInput, setShiftStartDateInput] = useState<string>("");
  const [shiftStartDateDisplay, setShiftStartDateDisplay] =
    useState<string>("");
  const [shiftCategoryNameInput, setShiftCategoryNameInput] =
    useState<string>("");
  const [deleteCategoryConfirmId, setDeleteCategoryConfirmId] = useState<
    number | null
  >(null);
  const [moveCategoryId, setMoveCategoryId] = useState<number | null>(null);
  const [moveCategoryTargetParentId, setMoveCategoryTargetParentId] =
    useState<string>("");
  const [moveProjectId, setMoveProjectId] = useState<number | null>(null);
  const [moveProjectSourceCategoryId, setMoveProjectSourceCategoryId] =
    useState<number | null>(null);
  const [moveProjectTargetCategoryId, setMoveProjectTargetCategoryId] =
    useState<string>("");
  const [isCapacityModalOpen, setIsCapacityModalOpen] =
    useState<boolean>(false);
  const [capacityDraft, setCapacityDraft] = useState<MonthlyCapacity>(() =>
    getConfiguredMonthlyCapacity(),
  );
  const [isUserManagementModalOpen, setIsUserManagementModalOpen] =
    useState<boolean>(false);
  const [managedUsers, setManagedUsers] = useState<ManagedUser[]>([]);
  const [isUserManagementLoading, setIsUserManagementLoading] =
    useState<boolean>(false);
  const [userManagementError, setUserManagementError] =
    useState<string>("");
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [isCategoryCommentsModalOpen, setIsCategoryCommentsModalOpen] =
    useState<boolean>(false);
  const [categoryCommentsTarget, setCategoryCommentsTarget] =
    useState<Category | null>(null);

  // Hidden file input ref
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Get flattened categories from schedule (includes subcategories)
  const categories = useMemo<Category[]>(() => {
    const cats = schedule.getFlattenedCategories();
    console.log("Categories:", cats);
    console.log("Number of categories:", cats.length);
    return cats;
  }, [schedule]);

  const formatDateForLocalInput = (date?: Date): string => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const isValidIsoDate = (isoDate: string): boolean => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return false;
    const [year, month, day] = isoDate.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  const lightenHexColor = (hex: string, amount = 0.45): string => {
    const normalized = hex.replace("#", "");
    if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) return hex;

    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);

    const mix = (value: number) => Math.round(value + (255 - value) * amount);
    const toHex = (value: number) => value.toString(16).padStart(2, "0");

    return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
  };

  const getSyntheticCategoryBounds = (
    category: Category,
  ): { first?: Date; last?: Date } => {
    const includedStatuses = [
      Status.BACKLOG,
      Status.UNDER_DISCUSSION,
      Status.PLANNED,
      Status.IN_PROGRESS,
    ];

    const timestamps = category
      .getAllProjects()
      .filter((project) => includedStatuses.includes(project.status))
      .flatMap((project) => {
        const dates: Date[] = [];
        if (project.startDate) dates.push(project.startDate);
        if (project.endDate) dates.push(project.endDate);
        return dates;
      })
      .map((date) => date.getTime());

    if (timestamps.length === 0) {
      return {};
    }

    return {
      first: new Date(Math.min(...timestamps)),
      last: new Date(Math.max(...timestamps)),
    };
  };

  const shiftCategory = useMemo(() => {
    if (shiftCategoryId === null) return null;
    return (
      categories.find(
        (category: Category) => category.id === shiftCategoryId,
      ) ?? null
    );
  }, [categories, shiftCategoryId]);

  const shiftCategoryBounds = useMemo(() => {
    if (!shiftCategory) return null;
    return getSyntheticCategoryBounds(shiftCategory);
  }, [shiftCategory]);

  const shiftCategoryProjects = useMemo<Project[]>(() => {
    if (!shiftCategory) return [];
    return shiftCategory.getAllProjects();
  }, [shiftCategory]);

  const clientProjectSelectionStats = useMemo<
    Record<string, { selectedCount: number; totalCount: number }>
  >(() => {
    const totalCount = shiftCategoryProjects.length;

    return clientProjects.reduce(
      (accumulator, clientProject) => {
        const selectedCount = shiftCategoryProjects.reduce((count, project) => {
          return (project.clientProjectIds || []).includes(clientProject.id)
            ? count + 1
            : count;
        }, 0);

        accumulator[clientProject.id] = {
          selectedCount,
          totalCount,
        };

        return accumulator;
      },
      {} as Record<string, { selectedCount: number; totalCount: number }>,
    );
  }, [clientProjects, shiftCategoryProjects]);

  const projectsCountByStatus = useMemo<Record<Status, number>>(() => {
    const counts = Object.values(Status).reduce(
      (acc, status) => {
        acc[status] = 0;
        return acc;
      },
      {} as Record<Status, number>,
    );

    shiftCategoryProjects.forEach((project) => {
      counts[project.status] += 1;
    });

    return counts;
  }, [shiftCategoryProjects]);

  const loadByStatusAndCategory = useMemo<
    Record<Status, Record<WorkCategory, number>>
  >(() => {
    const matrix = Object.values(Status).reduce(
      (statusAcc, status) => {
        statusAcc[status] = Object.values(WorkCategory).reduce(
          (workAcc, workCat) => {
            workAcc[workCat] = 0;
            return workAcc;
          },
          {} as Record<WorkCategory, number>,
        );
        return statusAcc;
      },
      {} as Record<Status, Record<WorkCategory, number>>,
    );

    shiftCategoryProjects.forEach((project) => {
      Object.values(WorkCategory).forEach((workCat) => {
        matrix[project.status][workCat] +=
          project.effortBreakdown[workCat] || 0;
      });
    });

    return matrix;
  }, [shiftCategoryProjects]);

  const closeShiftModal = () => {
    setShiftCategoryId(null);
    setShiftStartDateInput("");
    setShiftStartDateDisplay("");
    setShiftCategoryNameInput("");
    setDeleteCategoryConfirmId(null);
  };

  const openCategoryCommentsModal = (category: Category) => {
    setCategoryCommentsTarget(category);
    setIsCategoryCommentsModalOpen(true);
  };

  const closeCategoryCommentsModal = () => {
    setIsCategoryCommentsModalOpen(false);
    setCategoryCommentsTarget(null);
  };

  const openShiftModal = (category: Category) => {
    if (category.id === null) return;
    const bounds = getSyntheticCategoryBounds(category);
    const defaultIso = formatDateForLocalInput(bounds.first);
    setShiftCategoryId(category.id);
    setShiftStartDateInput(defaultIso);
    setShiftStartDateDisplay(defaultIso ? parseDateToFrench(defaultIso) : "");
    setShiftCategoryNameInput(category.name);
  };

  const applyCategoryNameChange = () => {
    if (!shiftCategory || shiftCategory.id === null || !onCategoryNameChange)
      return;

    const trimmedName = shiftCategoryNameInput.trim();
    if (!trimmedName) {
      alert("Category name cannot be empty.");
      return;
    }

    if (trimmedName === shiftCategory.name) return;
    onCategoryNameChange(shiftCategory.id, trimmedName);
  };

  const openDeleteCategoryConfirm = () => {
    if (!shiftCategory || shiftCategory.id === null || !onDeleteCategory)
      return;
    setDeleteCategoryConfirmId(shiftCategory.id);
  };

  const closeDeleteCategoryConfirm = () => {
    setDeleteCategoryConfirmId(null);
  };

  const applyDeleteCategory = () => {
    if (deleteCategoryConfirmId === null || !onDeleteCategory) return;
    onDeleteCategory(deleteCategoryConfirmId);
    setDeleteCategoryConfirmId(null);
    closeShiftModal();
  };

  const canMoveUp = useMemo(() => {
    if (moveCategoryId === null) return false;
    const movingCat = categories.find((c) => c.id === moveCategoryId);
    if (!movingCat) return false;
    const siblings = categories.filter(
      (c) => c.parentId === movingCat.parentId,
    );
    const index = siblings.findIndex((c) => c.id === movingCat.id);
    return index > 0;
  }, [categories, moveCategoryId]);

  const canMoveDown = useMemo(() => {
    if (moveCategoryId === null) return false;
    const movingCat = categories.find((c) => c.id === moveCategoryId);
    if (!movingCat) return false;
    const siblings = categories.filter(
      (c) => c.parentId === movingCat.parentId,
    );
    const index = siblings.findIndex((c) => c.id === movingCat.id);
    return index >= 0 && index < siblings.length - 1;
  }, [categories, moveCategoryId]);

  const deleteCategoryTarget = useMemo(() => {
    if (deleteCategoryConfirmId === null) return null;
    return (
      categories.find((category) => category.id === deleteCategoryConfirmId) ??
      null
    );
  }, [categories, deleteCategoryConfirmId]);

  const deleteCategorySubcategoryCount = useMemo(() => {
    if (!deleteCategoryTarget) return 0;
    return Math.max(0, deleteCategoryTarget.getAllCategories().length - 1);
  }, [deleteCategoryTarget]);

  const deleteCategoryProjectCount = useMemo(() => {
    if (!deleteCategoryTarget) return 0;
    return deleteCategoryTarget.getAllProjects().length;
  }, [deleteCategoryTarget]);

  const closeMoveModal = () => {
    setMoveCategoryId(null);
    setMoveCategoryTargetParentId("");
  };

  const closeMoveProjectModal = () => {
    setMoveProjectId(null);
    setMoveProjectSourceCategoryId(null);
    setMoveProjectTargetCategoryId("");
  };

  const openMoveModal = (category: Category) => {
    if (category.id === null) return;
    setMoveCategoryId(category.id);
    // Will be set to first valid option via useMemo/useEffect
    setMoveCategoryTargetParentId("");
  };

  const openMoveProjectModal = (project: Project, sourceCategory: Category) => {
    if (project.id === null || sourceCategory.id === null) return;
    setMoveProjectId(project.id);
    setMoveProjectSourceCategoryId(sourceCategory.id);
    setMoveProjectTargetCategoryId("");
  };

  const collectCategoryDescendantIds = (category: Category): Set<number> => {
    const descendants = new Set<number>();

    const walk = (node: Category) => {
      descendants.add(node.id!);
      node.getSubcategories().forEach(walk);
    };

    category.getSubcategories().forEach(walk);
    return descendants;
  };

  const moveCategoryOptions = useMemo(() => {
    if (moveCategoryId === null) return [] as Category[];

    const selected = categories.find((c) => c.id === moveCategoryId);
    if (!selected) return [] as Category[];

    // IDs that cannot be a new parent: the category itself + all its descendants
    const forbiddenIds = collectCategoryDescendantIds(selected);
    forbiddenIds.add(selected.id!);

    const options: Category[] = [];
    const seen = new Set<number>();

    // DOWN: Siblings - other children of the same parent
    const siblings = categories.filter(
      (c) => c.parentId === selected.parentId && c.id !== selected.id,
    );

    // LATERAL: Parent's siblings - other children of the grandparent
    let parentSiblings: Category[] = [];
    if (selected.parentId !== null) {
      const parent = categories.find((c) => c.id === selected.parentId);
      if (parent) {
        parentSiblings = categories.filter(
          (c) => c.parentId === parent.parentId && c.id !== parent.id,
        );
      }
    }

    // UP: The grandparent itself (parent's parent)
    let grandparent: Category | null = null;
    if (selected.parentId !== null) {
      const parent = categories.find((c) => c.id === selected.parentId);
      if (parent && parent.parentId !== null) {
        grandparent = categories.find((c) => c.id === parent.parentId) || null;
      }
    }

    // Merge all options and deduplicate, excluding forbidden ids
    for (const cat of [
      ...siblings,
      ...parentSiblings,
      ...(grandparent ? [grandparent] : []),
    ]) {
      if (cat.id !== null && !forbiddenIds.has(cat.id!) && !seen.has(cat.id!)) {
        seen.add(cat.id!);
        options.push(cat);
      }
    }
    return options;
  }, [categories, moveCategoryId]);

  const moveProjectOptions = useMemo(() => {
    if (moveProjectSourceCategoryId === null) return [] as Category[];
    const sourceCategory = categories.find(
      (c) => c.id === moveProjectSourceCategoryId,
    );
    if (!sourceCategory) return [] as Category[];

    const options: Category[] = [];
    const seen = new Set<number>();

    const siblings = categories.filter(
      (c) =>
        c.parentId === sourceCategory.parentId && c.id !== sourceCategory.id,
    );

    let parentSiblings: Category[] = [];
    let directParent: Category | null = null;
    let grandparent: Category | null = null;
    if (sourceCategory.parentId !== null) {
      const parent = categories.find((c) => c.id === sourceCategory.parentId);
      if (parent) {
        directParent = parent;
        parentSiblings = categories.filter(
          (c) => c.parentId === parent.parentId && c.id !== parent.id,
        );

        if (parent.parentId !== null) {
          grandparent =
            categories.find((c) => c.id === parent.parentId) || null;
        }
      }
    }

    for (const cat of [
      ...siblings,
      ...(directParent ? [directParent] : []),
      ...parentSiblings,
      ...(grandparent ? [grandparent] : []),
    ]) {
      if (cat.id !== null && !seen.has(cat.id)) {
        seen.add(cat.id);
        options.push(cat);
      }
    }

    return options;
  }, [categories, moveProjectSourceCategoryId]);

  const movingProject = useMemo(() => {
    if (moveProjectId === null) return null;
    return (
      schedule
        .getAllProjects()
        .find((project: Project) => project.id === moveProjectId) ?? null
    );
  }, [moveProjectId, schedule]);

  // Check if moving to top level is valid (parent is at top level)
  const canMoveToTopLevel = useMemo(() => {
    if (moveCategoryId === null) return false;
    const selected = categories.find((c) => c.id === moveCategoryId);
    if (!selected) return false;
    // Can move to top level if parent is at top level (parentId === null means nested under top level)
    if (selected.parentId === null) return false; // Already at top level
    const parent = categories.find((c) => c.id === selected.parentId);
    return parent && parent.parentId === null;
  }, [categories, moveCategoryId]);

  // Auto-select first available option when move modal opens
  React.useEffect(() => {
    if (moveCategoryId !== null && moveCategoryTargetParentId === "") {
      // Prefer "Top level" if available, otherwise first category option
      if (canMoveToTopLevel) {
        setMoveCategoryTargetParentId("root");
      } else if (moveCategoryOptions.length > 0) {
        setMoveCategoryTargetParentId(String(moveCategoryOptions[0].id));
      }
    }
  }, [
    moveCategoryId,
    moveCategoryOptions,
    moveCategoryTargetParentId,
    canMoveToTopLevel,
  ]);

  React.useEffect(() => {
    if (
      moveProjectId !== null &&
      moveProjectTargetCategoryId === "" &&
      moveProjectOptions.length > 0
    ) {
      setMoveProjectTargetCategoryId(String(moveProjectOptions[0].id));
    }
  }, [moveProjectId, moveProjectOptions, moveProjectTargetCategoryId]);

  const applyCategoryMove = () => {
    if (moveCategoryId === null || !onMoveCategory) return;

    let targetParentId: number | null = null;
    if (moveCategoryTargetParentId === "root") {
      targetParentId = null;
    } else {
      targetParentId = Number(moveCategoryTargetParentId);
      if (Number.isNaN(targetParentId)) {
        alert("Please choose a valid destination.");
        return;
      }
    }

    onMoveCategory(moveCategoryId, targetParentId);
    closeMoveModal();
  };

  const applyProjectMove = () => {
    if (
      moveProjectId === null ||
      moveProjectSourceCategoryId === null ||
      !onMoveProject
    ) {
      return;
    }

    const targetCategoryId = Number(moveProjectTargetCategoryId);
    if (!moveProjectTargetCategoryId || Number.isNaN(targetCategoryId)) {
      alert("Please choose a valid destination.");
      return;
    }

    onMoveProject(moveProjectId, moveProjectSourceCategoryId, targetCategoryId);
    closeMoveProjectModal();
  };

  const applyCategoryShift = () => {
    if (!shiftCategory || shiftCategory.id === null || !onShiftCategoryTiming)
      return;

    const targetDate = shiftStartDateInput;
    if (!targetDate) {
      alert("Please choose a target date.");
      return;
    }

    onShiftCategoryTiming({
      categoryId: shiftCategory.id,
      boundary: "start",
      targetDate,
    });
    closeShiftModal();
  };

  const handleCategoryClientProjectLabelToggle = (
    clientProjectId: string,
    shouldAssign: boolean,
  ) => {
    if (
      !shiftCategory ||
      shiftCategory.id === null ||
      !onCategoryClientProjectLabelChange
    ) {
      return;
    }

    onCategoryClientProjectLabelChange(
      shiftCategory.id,
      clientProjectId,
      shouldAssign,
    );
  };

  // Toggle category collapse/expand
  const toggleCategory = (categoryId: number | null) => {
    if (categoryId === null) return;
    setCollapsedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Statuses that count toward workload (cannot be filtered out)
  const activeStatuses = [Status.PLANNED, Status.IN_PROGRESS];
  const secondaryStatuses: Status[] = [Status.BACKLOG, Status.UNDER_DISCUSSION];
  const filterableStatuses = allStatuses.filter(
    (status) => !activeStatuses.includes(status),
  );

  const selectedClientProject = useMemo(
    () =>
      clientProjects.find(
        (clientProject) => clientProject.id === selectedClientProjectId,
      ),
    [clientProjects, selectedClientProjectId],
  );

  const filteredClientProjects = useMemo(() => {
    const query = clientProjectSearch.trim().toLowerCase();
    if (!query) return clientProjects;

    return clientProjects.filter((clientProject) =>
      clientProject.name.toLowerCase().includes(query),
    );
  }, [clientProjects, clientProjectSearch]);

  const doesProjectMatchClientFilter = (project: Project): boolean => {
    if (selectedClientProjectId === ALL_CLIENT_PROJECTS) {
      return true;
    }

    return (project.clientProjectIds || []).includes(selectedClientProjectId);
  };

  // Check if a project should be visible based on status filter
  const isProjectVisible = (project: Project): boolean => {
    return (
      !hiddenStatuses.has(project.status) && doesProjectMatchClientFilter(project)
    );
  };

  const selectedWorkCategoryList = useMemo(
    () =>
      allWorkCategories.filter((category) =>
        selectedWorkCategories.has(category),
      ),
    [allWorkCategories, selectedWorkCategories],
  );

  const isAllWorkCategoriesSelected =
    selectedWorkCategories.size === allWorkCategories.length;

  const getProjectEffortForSelectedCategories = (project: Project): number => {
    return selectedWorkCategoryList.reduce(
      (sum, category) => sum + project.getEffortForCategory(category),
      0,
    );
  };

  const getProjectMonthlyEffortDistribution = (
    project: Project,
    monthsToUse: Month[],
  ): Record<number, number> => {
    const monthly: Record<number, number> = {};

    monthsToUse.forEach((_, index) => {
      monthly[index] = 0;
    });

    selectedWorkCategoryList.forEach((category) => {
      const categoryDistribution = project.getMonthlyEffortDistribution(
        monthsToUse,
        category,
      );

      monthsToUse.forEach((_, index) => {
        monthly[index] += categoryDistribution[index] || 0;
      });
    });

    return monthly;
  };

  const getProjectsMonthlyEffort = (
    projects: Project[],
    monthsToUse: Month[],
    visibleStatuses?: Set<Status>,
  ): Record<number, number> => {
    const monthly: Record<number, number> = {};

    monthsToUse.forEach((_, index) => {
      monthly[index] = 0;
    });

    projects.forEach((project) => {
      if (visibleStatuses && !visibleStatuses.has(project.status)) {
        return;
      }

      const projectMonthly = getProjectMonthlyEffortDistribution(
        project,
        monthsToUse,
      );
      monthsToUse.forEach((_, index) => {
        monthly[index] += projectMonthly[index] || 0;
      });
    });

    return monthly;
  };

  const getCapacityForSelectedCategories = (month: Month): number => {
    if (isAllWorkCategoriesSelected) {
      return getTotalMonthlyCapacity(month.year, month.month);
    }

    return selectedWorkCategoryList.reduce(
      (sum, category) =>
        sum + getMonthlyCapacity(category, month.year, month.month),
      0,
    );
  };

  const getUtilizationForSelectedCategories = (
    effort: number,
    month: Month,
  ): number => {
    const capacity = getCapacityForSelectedCategories(month);
    return capacity > 0 ? (effort / capacity) * 100 : 0;
  };

  const isOverCapacityForSelectedCategories = (
    effort: number,
    month: Month,
  ): boolean => {
    return effort > getCapacityForSelectedCategories(month);
  };

  const getEffortBreakdownByStatus = (
    projects: Project[],
  ): Record<Status, number> => {
    return Object.values(Status).reduce(
      (acc, status) => {
        acc[status] = projects
          .filter((project) => project.status === status)
          .reduce(
            (sum, project) =>
              sum + getProjectEffortForSelectedCategories(project),
            0,
          );
        return acc;
      },
      {} as Record<Status, number>,
    );
  };

  const getSecondaryEffort = (
    statusBreakdown: Record<Status, number>,
  ): number => {
    return secondaryStatuses.reduce((sum, status) => {
      if (hiddenStatuses.has(status)) return sum;
      return sum + (statusBreakdown[status] || 0);
    }, 0);
  };

  const formatStatusBreakdownTooltip = (
    statusBreakdown: Record<Status, number>,
  ): string => {
    return Object.values(Status)
      .map(
        (status) =>
          `${STATUS_LABELS[status]}: ${statusBreakdown[status].toFixed(1)} jh`,
      )
      .join("\n");
  };

  const scheduleProjects = useMemo<Project[]>(
    () => schedule.getAllProjects().filter(doesProjectMatchClientFilter),
    [schedule, selectedClientProjectId],
  );
  const scheduleActiveProjects = useMemo<Project[]>(
    () =>
      scheduleProjects.filter(
        (project) =>
          project.status === Status.PLANNED ||
          project.status === Status.IN_PROGRESS,
      ),
    [scheduleProjects],
  );
  const scheduleStatusBreakdown = getEffortBreakdownByStatus(scheduleProjects);

  // Get the default date range from schedule
  const defaultDateRange = useMemo(() => schedule.getDateRange(), [schedule]);

  // Apply custom date range if specified, otherwise use default
  // When no custom start is set, clamp the start to the beginning of the current month
  const {
    startDate: timelineStartDate,
    endDate: timelineEndDate,
    months,
  } = useMemo(() => {
    const hasCustomStart = customStartDate !== "";
    const hasCustomEnd = customEndDate !== "";

    const startOfCurrentMonth = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      1,
    );

    const start = hasCustomStart
      ? new Date(customStartDate)
      : startOfCurrentMonth;

    const end = hasCustomEnd
      ? new Date(customEndDate)
      : defaultDateRange.endDate;

    // Generate months for the range
    const monthsList: Month[] = [];
    let currentYear = start.getFullYear();
    let currentMonth = start.getMonth();
    const endYear = end.getFullYear();
    const endMonth = end.getMonth();

    while (
      currentYear < endYear ||
      (currentYear === endYear && currentMonth <= endMonth)
    ) {
      const monthDate = new Date(currentYear, currentMonth, 1);
      monthsList.push({
        year: currentYear,
        month: currentMonth,
        label: monthDate.toLocaleDateString("fr-FR", {
          month: "short",
          year: "numeric",
        }),
      });

      currentMonth++;
      if (currentMonth > 11) {
        currentMonth = 0;
        currentYear++;
      }
    }

    return { startDate: start, endDate: end, months: monthsList };
  }, [defaultDateRange, customStartDate, customEndDate]);

  // Calculate monthly effort distribution (filtered by work category)
  const monthlyEffort = useMemo(() => {
    const monthly: Record<number, number> = {};

    months.forEach((_, index) => {
      monthly[index] = 0;
    });

    scheduleActiveProjects.forEach((project) => {
      const projectMonthly = months.reduce(
        (acc, _, index) => {
          acc[index] = 0;
          return acc;
        },
        {} as Record<number, number>,
      );

      selectedWorkCategoryList.forEach((category) => {
        const categoryDistribution = project.getMonthlyEffortDistribution(
          months,
          category,
        );

        months.forEach((_, index) => {
          projectMonthly[index] += categoryDistribution[index] || 0;
        });
      });

      months.forEach((_, index) => {
        monthly[index] += projectMonthly[index] || 0;
      });
    });

    return monthly;
  }, [months, scheduleActiveProjects, selectedWorkCategoryList]);

  const scheduleMonthlyStatusBreakdown = useMemo<
    Record<number, Record<Status, number>>
  >(() => {
    const breakdown: Record<number, Record<Status, number>> = {};

    months.forEach((_, index) => {
      breakdown[index] = Object.values(Status).reduce(
        (acc, status) => {
          acc[status] = 0;
          return acc;
        },
        {} as Record<Status, number>,
      );
    });

    scheduleProjects.forEach((project) => {
      const monthlyDistribution = months.reduce(
        (acc, _, index) => {
          acc[index] = 0;
          return acc;
        },
        {} as Record<number, number>,
      );

      selectedWorkCategoryList.forEach((category) => {
        const categoryDistribution = project.getMonthlyEffortDistribution(
          months,
          category,
        );

        months.forEach((_, index) => {
          monthlyDistribution[index] += categoryDistribution[index] || 0;
        });
      });

      months.forEach((_, index) => {
        breakdown[index][project.status] += monthlyDistribution[index] || 0;
      });
    });

    return breakdown;
  }, [months, scheduleProjects, selectedWorkCategoryList]);

  const handleWorkCategoryToggle = (category: WorkCategory) => {
    setSelectedWorkCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        if (next.size === 1) {
          return prev;
        }
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleWorkCategorySelectAll = () => {
    setSelectedWorkCategories(new Set(allWorkCategories));
  };

  const handleStatusToggle = (status: Status) => {
    if (activeStatuses.includes(status)) {
      return;
    }

    setHiddenStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const handleStatusSelectAll = () => {
    setHiddenStatuses(new Set());
  };

  const handleClientProjectSelect = (clientProjectId: string) => {
    setSelectedClientProjectId(clientProjectId);
    setClientProjectSearch("");
    setIsClientProjectFilterOpen(false);
  };

  const visibleStatuses = allStatuses.filter(
    (status) => activeStatuses.includes(status) || !hiddenStatuses.has(status),
  );

  const visibleOptionalStatuses = filterableStatuses.filter(
    (status) => !hiddenStatuses.has(status),
  );

  const workCategoryFilterSummary = isAllWorkCategoriesSelected
    ? "All work categories"
    : selectedWorkCategoryList.length === 1
      ? WORK_CATEGORY_LABELS[selectedWorkCategoryList[0]]
      : `${selectedWorkCategoryList.length} work categories`;

  const statusFilterSummary =
    hiddenStatuses.size === 0
      ? "All statuses"
      : visibleOptionalStatuses.length === 0
        ? "Active statuses only"
        : visibleStatuses.length === 1
          ? STATUS_LABELS[visibleStatuses[0]]
          : `${visibleStatuses.length} statuses`;

  const clientProjectFilterSummary =
    selectedClientProjectId === ALL_CLIENT_PROJECTS
      ? "All client projects"
      : selectedClientProject?.name || "Unknown client project";

  const scheduleTotalLabel = selectedClientProject
    ? `${selectedClientProject.name} Total`
    : "Schedule Total";

  const displayedBreakdownCategories = isAllWorkCategoriesSelected
    ? allWorkCategories
    : selectedWorkCategoryList;

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        workCategoryFilterRef.current &&
        !workCategoryFilterRef.current.contains(target)
      ) {
        setIsWorkCategoryFilterOpen(false);
      }

      if (
        statusFilterRef.current &&
        !statusFilterRef.current.contains(target)
      ) {
        setIsStatusFilterOpen(false);
      }

      if (
        clientProjectFilterRef.current &&
        !clientProjectFilterRef.current.contains(target)
      ) {
        setIsClientProjectFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const isCurrentOrFutureMonth = (month: Month): boolean => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    return (
      month.year > currentYear ||
      (month.year === currentYear && month.month >= currentMonth)
    );
  };

  const getDisplayedTotalFromMonthly = (
    monthly: Record<number, number>,
  ): number => {
    return months.reduce((sum, month, index) => {
      if (totalDisplayMode === "remaining" && !isCurrentOrFutureMonth(month)) {
        return sum;
      }
      return sum + (monthly[index] || 0);
    }, 0);
  };

  const handleResetDateRange = () => {
    setCustomStartDate("");
    setCustomEndDate("");
    setCustomStartDateDisplay("");
    setCustomEndDateDisplay("");
  };

  const handleExportCSV = () => {
    exportSchedule(schedule);
  };

  const handleImportButtonClick = () => {
    if (!canEdit) return;
    fileInputRef.current?.click();
  };

  const openCapacityModal = () => {
    if (!canEdit || !canViewWorkload) return;
    setCapacityDraft(getConfiguredMonthlyCapacity());
    setIsCapacityModalOpen(true);
  };

  const closeCapacityModal = () => {
    setIsCapacityModalOpen(false);
  };

  const handleCapacityDraftChange = (category: WorkCategory, value: string) => {
    const parsed = Number(value);
    const normalized = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    setCapacityDraft((prev) => ({
      ...prev,
      [category]: normalized,
    }));
  };

  const applyCapacityConfig = () => {
    setConfiguredMonthlyCapacity(capacityDraft);
    onCapacityConfigChange?.();
    setIsCapacityModalOpen(false);
  };

  const resetCapacityConfig = () => {
    resetConfiguredMonthlyCapacity();
    onCapacityConfigChange?.();
    setCapacityDraft(getConfiguredMonthlyCapacity());
  };

  const roleLabelMap: Record<UserRole, string> = {
    default: "Default",
    viewer: "Viewer",
    admin: "Admin",
  };

  const formatDateTime = (isoDate: string): string => {
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) return "—";
    return parsed.toLocaleString();
  };

  const loadConnectedUsers = async () => {
    if (!canManageUsers) return;
    setIsUserManagementLoading(true);
    setUserManagementError("");

    try {
      const users = await listConnectedUsers();
      setManagedUsers(users);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load users";
      setUserManagementError(message);
    } finally {
      setIsUserManagementLoading(false);
    }
  };

  const openUserManagementModal = async () => {
    if (!canManageUsers) return;
    setIsUserManagementModalOpen(true);
    await loadConnectedUsers();
  };

  const closeUserManagementModal = () => {
    setIsUserManagementModalOpen(false);
  };

  const handleUserRoleChange = async (userId: string, role: UserRole) => {
    setUpdatingUserId(userId);
    setUserManagementError("");
    try {
      const updatedUser = await updateUserRole(userId, role);
      setManagedUsers((prev) =>
        prev.map((user) => (user.id === updatedUser.id ? updatedUser : user)),
      );

      if (authUser?.id === updatedUser.id) {
        setAuthUser({
          ...authUser,
          role: updatedUser.role,
          permissions:
            updatedUser.role === "admin"
              ? ["schedule.read", "schedule.write", "workload.view", "admin.access"]
              : updatedUser.role === "viewer"
              ? ["schedule.read", "workload.view"]
              : ["schedule.read"],
        });
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update role";
      setUserManagementError(message);
      await loadConnectedUsers();
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!canEdit) return;
    const file = event.target.files?.[0];
    if (file) {
      await onImportCSV(file);
      // Reset file input so the same file can be imported again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  console.log(
    "Rendering ProjectSchedule with",
    categories.length,
    "categories",
  );

  useEffect(() => {
    const updateStickyOffsets = () => {
      const container = containerRef.current;
      if (!container) return;

      const controlsHeight = controlsRef.current?.offsetHeight ?? 0;
      const headerHeight = headerRowRef.current?.offsetHeight ?? 0;
      const totalRowHeight = totalRowRef.current?.offsetHeight ?? 30;
      const breakdownRowHeight = breakdownRowRef.current?.offsetHeight ?? 30;

      container.style.setProperty(
        "--controls-sticky-height",
        `${controlsHeight}px`,
      );
      container.style.setProperty("--header-row-height", `${headerHeight}px`);
      container.style.setProperty("--total-row-height", `${totalRowHeight}px`);
      container.style.setProperty(
        "--breakdown-row-height",
        `${breakdownRowHeight}px`,
      );
    };

    updateStickyOffsets();

    const resizeObserver = new ResizeObserver(() => {
      updateStickyOffsets();
    });

    if (controlsRef.current) {
      resizeObserver.observe(controlsRef.current);
    }

    if (headerRowRef.current) {
      resizeObserver.observe(headerRowRef.current);
    }

    if (totalRowRef.current) {
      resizeObserver.observe(totalRowRef.current);
    }

    if (breakdownRowRef.current) {
      resizeObserver.observe(breakdownRowRef.current);
    }

    window.addEventListener("resize", updateStickyOffsets);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateStickyOffsets);
    };
  }, [
    months.length,
    selectedWorkCategories,
    totalDisplayMode,
    hiddenStatuses,
    isTotalCollapsed,
  ]);

  return (
    <div className="schedule-container" ref={containerRef}>
      <div className="add-project-section" ref={controlsRef}>
        <div className="controls-left">
          <AdminPanel
            onExportCSV={handleExportCSV}
            onImportCSV={handleImportButtonClick}
            canEdit={canEdit}
            onOpenBackup={canEdit ? onOpenBackup : undefined}
            canManageUsers={canManageUsers}
            onOpenUserManagement={
              canManageUsers ? openUserManagementModal : undefined
            }
            onOpenClientProjects={
              canEdit && onCreateClientProject
                ? () => setIsClientProjectModalOpen(true)
                : undefined
            }
          />

          {canEdit && onAddTopLevelCategory && (
            <button
              type="button"
              className="top-level-category-button"
              onClick={onAddTopLevelCategory}
              title="Add a new top-level category"
            >
              + Category
            </button>
          )}

          {canEdit && canViewWorkload && (
            <button
              type="button"
              className="capacity-config-button"
              onClick={openCapacityModal}
              title="Configure available workforce by work category"
            >
              ⚙ Workforce
            </button>
          )}

          <div className="date-range-filter">
            <label htmlFor="total-display-mode">Totals:</label>
            <select
              id="total-display-mode"
              value={totalDisplayMode}
              onChange={(e) =>
                setTotalDisplayMode(
                  e.target.value as "aggregated" | "remaining",
                )
              }
              className="total-display-select"
              title="Choose how totals are displayed"
            >
              <option value="remaining">Remaining total</option>
              <option value="aggregated">Aggregated total</option>
            </select>

            <label htmlFor="custom-start-date">Timeline:</label>
            <div className="date-input-wrapper">
              <input
                type="text"
                id="custom-start-date"
                placeholder="DD/MM/YYYY"
                value={
                  customStartDateDisplay ||
                  (customStartDate
                    ? parseDateToFrench(customStartDate)
                    : formatDateFrench(timelineStartDate))
                }
                onChange={(e) => {
                  setCustomStartDateDisplay(e.target.value);
                }}
                onBlur={() => {
                  const frenchDate = customStartDateDisplay.trim();
                  if (!frenchDate) {
                    setCustomStartDate("");
                    setCustomStartDateDisplay("");
                    return;
                  }

                  const isoDate = parseDateFromFrench(frenchDate);
                  if (isValidIsoDate(isoDate)) {
                    setCustomStartDate(isoDate);
                    setCustomStartDateDisplay(parseDateToFrench(isoDate));
                  } else {
                    setCustomStartDateDisplay(
                      customStartDate
                        ? parseDateToFrench(customStartDate)
                        : formatDateFrench(timelineStartDate),
                    );
                  }
                }}
                className="date-range-input"
                title={`Timeline start date (computed default: ${formatDateFrench(
                  timelineStartDate,
                )})`}
              />
            </div>
            <span className="date-range-separator">to</span>
            <div className="date-input-wrapper">
              <input
                type="text"
                id="custom-end-date"
                placeholder="DD/MM/YYYY"
                value={
                  customEndDateDisplay ||
                  (customEndDate
                    ? parseDateToFrench(customEndDate)
                    : formatDateFrench(timelineEndDate))
                }
                onChange={(e) => {
                  setCustomEndDateDisplay(e.target.value);
                }}
                onBlur={() => {
                  const frenchDate = customEndDateDisplay.trim();
                  if (!frenchDate) {
                    setCustomEndDate("");
                    setCustomEndDateDisplay("");
                    return;
                  }

                  const isoDate = parseDateFromFrench(frenchDate);
                  if (isValidIsoDate(isoDate)) {
                    setCustomEndDate(isoDate);
                    setCustomEndDateDisplay(parseDateToFrench(isoDate));
                  } else {
                    setCustomEndDateDisplay(
                      customEndDate
                        ? parseDateToFrench(customEndDate)
                        : formatDateFrench(timelineEndDate),
                    );
                  }
                }}
                className="date-range-input"
                title={`Timeline end date (computed default: ${formatDateFrench(
                  timelineEndDate,
                )})`}
              />
            </div>
            <button
              type="button"
              className="reset-date-button"
              onClick={handleResetDateRange}
              title="Reset to automatic date range"
            >
              ↻
            </button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <div className="filters-panel">
          <div
            className="filter-group compact-filter-group filter-dropdown"
            ref={workCategoryFilterRef}
          >
            <label>Work categories</label>
            <button
              type="button"
              className={`filter-dropdown-toggle ${
                isWorkCategoryFilterOpen ? "is-open" : ""
              }`}
              onClick={() => {
                setIsWorkCategoryFilterOpen((prev) => !prev);
                setIsStatusFilterOpen(false);
                setIsClientProjectFilterOpen(false);
              }}
              title="Choose visible work categories"
            >
              <span className="filter-dropdown-value">
                {workCategoryFilterSummary}
              </span>
              <span className="filter-dropdown-arrow">▾</span>
            </button>

            {isWorkCategoryFilterOpen && (
              <div className="filter-dropdown-menu">
                <button
                  type="button"
                  className="filter-select-all-button"
                  onClick={handleWorkCategorySelectAll}
                >
                  All work categories
                </button>

                {allWorkCategories.map((category) => (
                  <label key={category} className="filter-option">
                    <input
                      type="checkbox"
                      checked={selectedWorkCategories.has(category)}
                      onChange={() => handleWorkCategoryToggle(category)}
                    />
                    <span>{WORK_CATEGORY_LABELS[category]}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div
            className="filter-group compact-filter-group filter-dropdown status-filter"
            ref={statusFilterRef}
          >
            <label>Statuses</label>
            <button
              type="button"
              className={`filter-dropdown-toggle ${
                isStatusFilterOpen ? "is-open" : ""
              }`}
              onClick={() => {
                setIsStatusFilterOpen((prev) => !prev);
                setIsWorkCategoryFilterOpen(false);
                setIsClientProjectFilterOpen(false);
              }}
              title="Choose visible statuses"
            >
              <span className="filter-dropdown-value">
                {statusFilterSummary}
              </span>
              <span className="filter-dropdown-arrow">▾</span>
            </button>

            {isStatusFilterOpen && (
              <div className="filter-dropdown-menu">
                <button
                  type="button"
                  className="filter-select-all-button"
                  onClick={handleStatusSelectAll}
                >
                  All statuses
                </button>

                {allStatuses.map((status) => {
                  const isLocked = activeStatuses.includes(status);

                  return (
                    <label
                      key={status}
                      className={`filter-option ${isLocked ? "is-locked" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={isLocked || !hiddenStatuses.has(status)}
                        onChange={() => handleStatusToggle(status)}
                        disabled={isLocked}
                      />
                      <span>
                        {STATUS_LABELS[status]}
                        {isLocked ? " (always visible)" : ""}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div
            className="filter-group compact-filter-group filter-dropdown client-project-filter"
            ref={clientProjectFilterRef}
          >
            <label>Client projects</label>
            <button
              type="button"
              className={`filter-dropdown-toggle ${
                isClientProjectFilterOpen ? "is-open" : ""
              }`}
              onClick={() => {
                setIsClientProjectFilterOpen((prev) => !prev);
                setIsWorkCategoryFilterOpen(false);
                setIsStatusFilterOpen(false);
                setClientProjectSearch("");
              }}
              title="Choose visible client project"
            >
              <span className="filter-dropdown-value">
                {clientProjectFilterSummary}
              </span>
              <span className="filter-dropdown-arrow">▾</span>
            </button>

            {isClientProjectFilterOpen && (
              <div className="filter-dropdown-menu">
                <input
                  type="text"
                  className="client-project-filter-search"
                  placeholder="Search client project..."
                  value={clientProjectSearch}
                  onChange={(e) => setClientProjectSearch(e.target.value)}
                />

                <button
                  type="button"
                  className="filter-select-all-button"
                  onClick={() => handleClientProjectSelect(ALL_CLIENT_PROJECTS)}
                >
                  All client projects
                </button>

                {clientProjects.length === 0 && (
                  <p className="client-project-filter-empty">
                    No client projects available.
                  </p>
                )}

                {clientProjects.length > 0 && filteredClientProjects.length === 0 && (
                  <p className="client-project-filter-empty">
                    No matching client project.
                  </p>
                )}

                {filteredClientProjects.map((clientProject: ClientProject) => (
                  <label key={clientProject.id} className="filter-option">
                    <input
                      type="radio"
                      name="client-project-filter"
                      checked={selectedClientProjectId === clientProject.id}
                      onChange={() => handleClientProjectSelect(clientProject.id)}
                    />
                    <span>{clientProject.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="schedule-grid"
        style={{
          gridTemplateColumns: `330px 150px repeat(${months.length}, minmax(100px, 1fr))`,
        }}
      >
        {/* Header Row */}
        <div className="grid-cell header-cell" ref={headerRowRef}>
          Project / Category
        </div>
        <div className="grid-cell header-cell">Status</div>
        {months.map((month: Month, index: number) => (
          <div key={index} className="grid-cell header-cell month-header">
            {month.label}
          </div>
        ))}

        {/* Schedule Total Row - Moved to top */}
        <div
          className="grid-cell total-label-cell clickable schedule-total-row-cell"
          ref={totalRowRef}
          onClick={() => setIsTotalCollapsed(!isTotalCollapsed)}
          title={`Click to expand/collapse capacity breakdown\n${formatStatusBreakdownTooltip(
            scheduleStatusBreakdown,
          )}`}
        >
          <span className="category-name">
            <span className="collapse-icon">
              {isTotalCollapsed ? "▶" : "▼"}
            </span>
            <span className="total-label">{scheduleTotalLabel}</span>
          </span>
        </div>
        <div className="grid-cell total-cell schedule-total-row-cell"></div>
        {months.map((month: Month, index: number) => {
          const effort = monthlyEffort[index];
          const monthlyStatusBreakdown = scheduleMonthlyStatusBreakdown[index];
          const secondaryEffort = getSecondaryEffort(monthlyStatusBreakdown);
          const capacity = getCapacityForSelectedCategories(month);
          const overCapacity = isOverCapacityForSelectedCategories(
            effort,
            month,
          );
          const utilization = getUtilizationForSelectedCategories(
            effort,
            month,
          );

          return (
            <div
              key={index}
              className="grid-cell total-cell schedule-total-row-cell"
              title={`${effort.toFixed(
                1,
              )} / ${capacity} jh (${utilization.toFixed(0)}% capacity)${
                overCapacity ? " - OVER CAPACITY!" : ""
              }\n${formatStatusBreakdownTooltip(monthlyStatusBreakdown)}`}
            >
              <span className="total-value">
                {effort.toFixed(0)}
                {secondaryEffort > 0
                  ? ` (${secondaryEffort.toFixed(0)})`
                  : ""}{" "}
                jh
              </span>
              {overCapacity && (
                <span
                  className="capacity-warning-icon"
                  title={`Over capacity: ${effort.toFixed(
                    1,
                  )} jh planned but only ${capacity} jh available`}
                >
                  ⚠️
                </span>
              )}
            </div>
          );
        })}

        {/* Work Category Breakdown - only show if expanded */}
        {!isTotalCollapsed &&
          displayedBreakdownCategories.map((workCat, rowIndex) => {
            const categoryEffort = scheduleActiveProjects.reduce(
              (monthly, project) => {
                const projectMonthly = project.getMonthlyEffortDistribution(
                  months,
                  workCat,
                );

                months.forEach((_, index) => {
                  monthly[index] += projectMonthly[index] || 0;
                });

                return monthly;
              },
              months.reduce(
                (acc, _, index) => {
                  acc[index] = 0;
                  return acc;
                },
                {} as Record<number, number>,
              ),
            );

            return (
              <React.Fragment key={workCat}>
                <div
                  className="grid-cell work-category-breakdown-cell schedule-breakdown-row-cell"
                  ref={rowIndex === 0 ? breakdownRowRef : undefined}
                  style={{
                    ["--breakdown-row-index" as string]: rowIndex,
                  }}
                >
                  <span className="work-category-breakdown-name">
                    {WORK_CATEGORY_LABELS[workCat]}
                  </span>
                </div>
                <div
                  className="grid-cell work-category-breakdown-cell schedule-breakdown-row-cell"
                  style={{
                    ["--breakdown-row-index" as string]: rowIndex,
                  }}
                ></div>
                {months.map((month: Month, index: number) => {
                  const effort = categoryEffort[index];
                  const capacity = getMonthlyCapacity(
                    workCat,
                    month.year,
                    month.month,
                  );
                  const overCapacity = isOverCapacity(
                    effort,
                    workCat,
                    month.year,
                    month.month,
                  );
                  const utilization =
                    capacity > 0
                      ? getCapacityUtilization(
                          effort,
                          workCat,
                          month.year,
                          month.month,
                        )
                      : 0;

                  return (
                    <div
                      key={index}
                      className="grid-cell work-category-breakdown-cell schedule-breakdown-row-cell"
                      style={{
                        ["--breakdown-row-index" as string]: rowIndex,
                      }}
                      title={`${effort.toFixed(
                        1,
                      )} / ${capacity} jh (${utilization.toFixed(
                        0,
                      )}% capacity)${overCapacity ? " - OVER CAPACITY!" : ""}`}
                    >
                      <span className="work-category-breakdown-value">
                        {effort > 0
                          ? `${effort.toFixed(0)} / ${capacity}`
                          : "-"}
                      </span>
                      {overCapacity && effort > 0 && (
                        <span
                          className="capacity-warning-icon"
                          title={`Over capacity: ${effort.toFixed(
                            1,
                          )} jh planned but only ${capacity} jh available`}
                        >
                          ⚠️
                        </span>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}

        {/* Category and Project Rows */}
        {categories.map((category: Category) => {
          const isCollapsed = collapsedCategories.has(category.id ?? 0);
          const hasChildren =
            category.hasSubcategories() || category.hasProjects();
          const indentLevel = category.level;
          const indentPixels = indentLevel * 20; // 20px per level
          const projectBaseColor = category.color;
          const projectLightColor = lightenHexColor(projectBaseColor, 0.5);

          // Check if any ancestor is collapsed
          let isHidden = false;
          if (category.parentId !== null) {
            // Find parent in categories list and check if it's collapsed
            const checkAncestorCollapsed = (parentId: number): boolean => {
              if (collapsedCategories.has(parentId)) return true;
              const parent = categories.find(
                (c: Category) => c.id === parentId,
              );
              if (parent && parent.parentId !== null) {
                return checkAncestorCollapsed(parent.parentId);
              }
              return false;
            };
            isHidden = checkAncestorCollapsed(category.parentId);
          }

          const allCategoryProjects = category
            .getAllProjects()
            .filter(doesProjectMatchClientFilter);
          const categoryActiveProjects = allCategoryProjects.filter(
            (project) =>
              project.status === Status.PLANNED ||
              project.status === Status.IN_PROGRESS,
          );

          const categoryEffort = getProjectsMonthlyEffort(
            categoryActiveProjects,
            months,
          );
          // Only include "In Progress" and "Planned" projects in category total effort
          const categoryTotalEffort = categoryActiveProjects.reduce(
            (sum, p) => sum + getProjectEffortForSelectedCategories(p),
            0,
          );
          const displayedCategoryTotalEffort =
            totalDisplayMode === "remaining"
              ? getDisplayedTotalFromMonthly(categoryEffort)
              : categoryTotalEffort;
          const categoryStatusBreakdown =
            getEffortBreakdownByStatus(allCategoryProjects);
          const categorySecondaryEffort = getSecondaryEffort(
            categoryStatusBreakdown,
          );
          const categoryMonthlyStatusBreakdown: Record<
            number,
            Record<Status, number>
          > = {};

          months.forEach((_, index) => {
            categoryMonthlyStatusBreakdown[index] = Object.values(
              Status,
            ).reduce(
              (acc, status) => {
                acc[status] = 0;
                return acc;
              },
              {} as Record<Status, number>,
            );
          });

          allCategoryProjects.forEach((project) => {
            const monthlyDistribution = getProjectMonthlyEffortDistribution(
              project,
              months,
            );

            months.forEach((_, index) => {
              categoryMonthlyStatusBreakdown[index][project.status] +=
                monthlyDistribution[index] || 0;
            });
          });
          const visibleProjectCount =
            allCategoryProjects.filter(isProjectVisible).length;
          const hasActiveVisibilityFilter =
            hiddenStatuses.size > 0 ||
            selectedClientProjectId !== ALL_CLIENT_PROJECTS;
          const isFilteredOutByFilters =
            hasActiveVisibilityFilter && visibleProjectCount === 0;

          // Don't render if hidden by collapsed ancestor or if no subtree project matches active filters
          if (isHidden || isFilteredOutByFilters) {
            return null;
          }

          return (
            <React.Fragment key={category.id ?? 0}>
              {/* Category Header Row */}
              <div
                className={`grid-cell category-header-cell clickable`}
                onClick={() => toggleCategory(category.id)}
                title={`${
                  hasChildren
                    ? "Click to expand/collapse category"
                    : "Category (empty)"
                }\n${formatStatusBreakdownTooltip(categoryStatusBreakdown)}`}
                style={{
                  paddingLeft: `${indentPixels + 12}px`,
                  backgroundColor: category.color,
                }}
              >
                <span className="category-name">
                  {hasChildren && (
                    <span className="collapse-icon">
                      {isCollapsed ? "▶" : "▼"}
                    </span>
                  )}
                  {!hasChildren && (
                    <span className="collapse-icon" style={{ opacity: 0 }}>
                      ▶
                    </span>
                  )}
                  {category.name}
                </span>
                <span className="category-effort">
                  {displayedCategoryTotalEffort.toFixed(0)}
                  {categorySecondaryEffort > 0
                    ? ` (${categorySecondaryEffort.toFixed(0)})`
                    : ""}{" "}
                  jh
                </span>
                {canEdit && category.id !== null && (
                  <button
                    className="category-add-button"
                    title={`Add project in ${category.name}`}
                    aria-label={`Add project in ${category.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddProject(category.id!);
                    }}
                  >
                    +
                  </button>
                )}
                {onConvertCategoryToProject &&
                  category.level > 0 &&
                  category.hasProjects() && (
                    <button
                      className="convert-button"
                      title="Convert to project (merges all sub-projects into one)"
                      aria-label={`Convert category ${category.name} to project`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const confirmed = window.confirm(
                          `Convert category "${category.name}" to a single project?\n\nThis will merge all sub-project effort into one project and remove the category hierarchy.`,
                        );
                        if (!confirmed) return;
                        onConvertCategoryToProject(category.id!);
                      }}
                    >
                      ⊟
                    </button>
                  )}
                {category.id !== null && (
                  <button
                    className="category-shift-button"
                    title="Synthetic category information"
                    aria-label={`Open synthetic category information for ${category.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openShiftModal(category);
                    }}
                  >
                    👁️
                  </button>
                )}
                {onMoveCategory && category.id !== null && (
                  <button
                    className="category-move-button"
                    title="Move category"
                    aria-label={`Move category ${category.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openMoveModal(category);
                    }}
                  >
                    ↕
                  </button>
                )}
              </div>
              <div
                className={`grid-cell category-total-cell`}
                style={{ backgroundColor: category.color }}
              ></div>
              {months.map((month: Month, index: number) => {
                const effort = categoryEffort[index];
                const monthlyStatusBreakdown =
                  categoryMonthlyStatusBreakdown[index];
                const secondaryMonthlyEffort = getSecondaryEffort(
                  monthlyStatusBreakdown,
                );
                const hasLoad = effort > 0;
                const capacity = getCapacityForSelectedCategories(month);
                const overCapacity = hasLoad
                  ? isOverCapacityForSelectedCategories(effort, month)
                  : false;
                const utilization = hasLoad
                  ? getUtilizationForSelectedCategories(effort, month)
                  : 0;

                return (
                  <div
                    key={index}
                    className={`grid-cell category-total-cell ${
                      hasLoad ? "" : "category-total-cell-empty"
                    }`}
                    style={
                      hasLoad ? { backgroundColor: category.color } : undefined
                    }
                    title={
                      hasLoad
                        ? `${effort.toFixed(
                            1,
                          )} / ${capacity} jh (${utilization.toFixed(
                            0,
                          )}% capacity)${
                            overCapacity ? " - OVER CAPACITY!" : ""
                          }`
                        : undefined
                    }
                  >
                    <span className="category-total-value">
                      {hasLoad ? effort.toFixed(0) : ""}
                      {hasLoad && secondaryMonthlyEffort > 0
                        ? ` (${secondaryMonthlyEffort.toFixed(0)})`
                        : ""}
                    </span>
                    {hasLoad && overCapacity && (
                      <span
                        className="capacity-warning-icon"
                        title={`Over capacity: ${effort.toFixed(
                          1,
                        )} jh planned but only ${capacity} jh available`}
                      >
                        ⚠️
                      </span>
                    )}
                  </div>
                );
              })}

              {/* Project Rows - show direct projects for any expanded category */}
              {!isCollapsed &&
                category
                  .getProjects()
                  .filter(isProjectVisible)
                  .map((project: Project) => {
                    // Check if project should be counted in totals (only "In Progress" and "Planned")
                    const isActiveProject =
                      project.status === Status.IN_PROGRESS ||
                      project.status === Status.PLANNED;

                    // Always calculate effort for display, but mark if it counts toward totals
                    const projectEffort = getProjectMonthlyEffortDistribution(
                      project,
                      months,
                    );

                    const projectTotalEffort =
                      getProjectEffortForSelectedCategories(project);
                    const displayedProjectTotalEffort =
                      totalDisplayMode === "remaining"
                        ? getDisplayedTotalFromMonthly(projectEffort)
                        : projectTotalEffort;

                    // Check for warnings
                    const projectHasWarnings = hasWarnings(project);
                    const primaryWarning = getPrimaryWarning(project);
                    const warningTooltip = getWarningTooltip(project);

                    return (
                      <React.Fragment key={project.id ?? 0}>
                        <div
                          className="grid-cell project-name-cell clickable"
                          onClick={() => onProjectClick(project)}
                          title={
                            warningTooltip ||
                            (isActiveProject
                              ? "Click to edit project"
                              : "Click to edit project (not counted in totals)")
                          }
                          style={{
                            opacity: isActiveProject ? 1 : 0.5,
                          }}
                        >
                          {projectHasWarnings && (
                            <span className="warning-icon">
                              {primaryWarning?.icon}
                              <span className="warning-tooltip">
                                {warningTooltip}
                              </span>
                            </span>
                          )}
                          <span
                            className="project-name"
                            style={{ color: projectBaseColor }}
                          >
                            {project.name}
                            <span className="edit-icon">✏️</span>
                          </span>
                          {onConvertProjectToCategory && (
                            <button
                              className="convert-button"
                              title="Convert to category (becomes a group you can add projects to)"
                              aria-label={`Convert project ${project.name} to category`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const confirmed = window.confirm(
                                  `Convert project "${project.name}" to a category?\n\nThe project will become a new category with the same name.`,
                                );
                                if (!confirmed) return;
                                onConvertProjectToCategory(project.id!);
                              }}
                            >
                              ⊞
                            </button>
                          )}
                          {onMoveProject && (
                            <button
                              className="project-move-button"
                              title="Move project"
                              aria-label={`Move project ${project.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                openMoveProjectModal(project, category);
                              }}
                            >
                              ↕
                            </button>
                          )}
                          <span
                            className="project-effort"
                            style={{
                              color: isActiveProject ? "#6b7280" : "#d1d5db",
                              textDecoration: isActiveProject
                                ? "none"
                                : "line-through",
                            }}
                          >
                            {displayedProjectTotalEffort > 0
                              ? displayedProjectTotalEffort.toFixed(0) + " jh"
                              : "-"}
                          </span>
                        </div>
                        <div className="grid-cell project-status-cell">
                          <select
                            className="status-select"
                            value={project.status}
                            disabled={!canEdit}
                            onChange={(e) => {
                              const updatedProject = project.update({
                                id: project.id,
                                name: project.name,
                                startDate: project.startDate,
                                endDate: project.endDate,
                                color: project.color,
                                effortBreakdown: project.effortBreakdown,
                              });
                              updatedProject.status = e.target.value as Status;
                              if (onProjectStatusChange) {
                                if (canEdit) {
                                  onProjectStatusChange(updatedProject);
                                }
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {Object.values(Status).map((status) => (
                              <option key={status} value={status}>
                                {STATUS_LABELS[status]}
                              </option>
                            ))}
                          </select>
                        </div>
                        {months.map((month: Month, index: number) => (
                          <div
                            key={index}
                            className="grid-cell project-timeline-cell"
                          >
                            {projectEffort[index] > 0 && (
                              <div
                                className={`project-bar ${
                                  !isActiveProject ? "inactive-project-bar" : ""
                                }`}
                                style={{
                                  backgroundColor: projectLightColor,
                                  opacity: isActiveProject ? 1 : 0.3,
                                  border: isActiveProject
                                    ? `1px solid ${projectBaseColor}`
                                    : `2px dashed ${projectBaseColor}`,
                                }}
                                onClick={() => onProjectClick(project)}
                                title={`${project.name}: ${projectEffort[
                                  index
                                ].toFixed(1)} jh${
                                  !isActiveProject
                                    ? " (not counted in totals)"
                                    : ""
                                }`}
                              >
                                <span
                                  className="project-bar-effort"
                                  style={{
                                    opacity: isActiveProject ? 1 : 0.7,
                                    textDecoration: isActiveProject
                                      ? "none"
                                      : "line-through",
                                  }}
                                >
                                  {projectEffort[index].toFixed(0)}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </React.Fragment>
                    );
                  })}
            </React.Fragment>
          );
        })}
      </div>

      {onCreateClientProject && (
        <ClientProjectModal
          isOpen={isClientProjectModalOpen}
          onClose={() => setIsClientProjectModalOpen(false)}
          clientProjects={clientProjects}
          onCreateClientProject={onCreateClientProject}
        />
      )}

      {shiftCategory && (
        <div className="category-shift-modal-overlay" onClick={closeShiftModal}>
          <div
            className="category-shift-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="category-shift-modal-header">
              <p className="category-shift-description">
                <strong>{shiftCategory.name}</strong>
              </p>

              {shiftCategory.id !== null && (
                <div className="category-shift-row">
                  <span className="category-shift-current-label">Name:</span>
                  <div className="category-shift-controls">
                    <input
                      type="text"
                      className="category-shift-date-input"
                      value={shiftCategoryNameInput}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setShiftCategoryNameInput(e.target.value)
                      }
                      onBlur={() => {
                        if (canEdit) {
                          applyCategoryNameChange();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (canEdit && e.key === "Enter") {
                          e.preventDefault();
                          applyCategoryNameChange();
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {shiftCategory.id !== null && (
                <div className="category-shift-color-row">
                  <span className="category-shift-current-label">Color:</span>
                  <label
                    className="category-shift-color-input-wrapper"
                    title={`Change color for ${shiftCategory.name}`}
                    aria-label={`Change color for ${shiftCategory.name}`}
                  >
                    <input
                      type="color"
                      className="category-shift-color-input"
                      value={shiftCategory.color}
                      onChange={(e) => {
                        if (canEdit && onCategoryColorChange) {
                          onCategoryColorChange(
                            shiftCategory.id!,
                            e.target.value,
                          );
                        }
                      }}
                      disabled={!canEdit}
                    />
                  </label>
                </div>
              )}

              {canEdit && shiftCategory.id !== null && onDeleteCategory && (
                <div className="category-shift-delete-row">
                  <button
                    className="category-shift-delete-button"
                    onClick={openDeleteCategoryConfirm}
                  >
                    Delete category
                  </button>
                </div>
              )}

              {shiftCategory.id !== null && (
                <div className="category-shift-comments-row">
                  <button
                    className="category-shift-comments-button"
                    onClick={() => openCategoryCommentsModal(shiftCategory)}
                    title="View and add comments for this category"
                  >
                    💬 Comments
                  </button>
                </div>
              )}
            </div>

            {shiftCategoryBounds?.first && shiftCategoryBounds?.last ? (
              <>
                <div className="category-shift-row">
                  <span className="category-shift-current-label">
                    Start date:
                  </span>
                  <span className="category-shift-current-value">
                    {formatDateFrench(shiftCategoryBounds.first)}
                  </span>
                </div>

                <div className="category-shift-row">
                  <span className="category-shift-current-label">
                    End date:
                  </span>
                  <span className="category-shift-current-value">
                    {formatDateFrench(shiftCategoryBounds.last)}
                  </span>
                </div>
              </>
            ) : (
              <p className="category-shift-empty">
                No eligible dates found in this category/subcategories.
              </p>
            )}

            <div className="category-shift-stats-section">
              <div className="category-shift-status-grid">
                {Object.values(Status).map((status) => (
                  <div key={status} className="category-shift-status-card">
                    <span className="category-shift-status-name">
                      {STATUS_LABELS[status]}
                    </span>
                    <span className="category-shift-status-count">
                      {projectsCountByStatus[status]}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="category-shift-stats-section">
              <h4>Load by status and task type</h4>
              <div className="category-shift-load-table-wrapper">
                <table className="category-shift-load-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      {Object.values(WorkCategory).map((workCat) => (
                        <th key={workCat}>{WORK_CATEGORY_LABELS[workCat]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(Status).map((status) => (
                      <tr key={status}>
                        <td>{STATUS_LABELS[status]}</td>
                        {Object.values(WorkCategory).map((workCat) => (
                          <td key={`${status}-${workCat}`}>
                            {Math.round(
                              loadByStatusAndCategory[status][workCat],
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="category-shift-stats-section">
              <h4>Client labels (recursive)</h4>
              {shiftCategoryProjects.length === 0 ? (
                <p className="category-shift-empty">
                  No projects in this category tree.
                </p>
              ) : clientProjects.length === 0 ? (
                <p className="category-shift-empty">
                  No client labels available.
                </p>
              ) : (
                <div className="category-client-label-list">
                  {clientProjects.map((clientProject) => {
                    const stats = clientProjectSelectionStats[clientProject.id] || {
                      selectedCount: 0,
                      totalCount: shiftCategoryProjects.length,
                    };

                    const isChecked =
                      stats.totalCount > 0 &&
                      stats.selectedCount === stats.totalCount;
                    const isPartial =
                      stats.selectedCount > 0 &&
                      stats.selectedCount < stats.totalCount;

                    return (
                      <label
                        key={clientProject.id}
                        className="category-client-label-option"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={!canEdit}
                          ref={(input) => {
                            if (input) {
                              input.indeterminate = isPartial;
                            }
                          }}
                          onChange={(event) => {
                            handleCategoryClientProjectLabelToggle(
                              clientProject.id,
                              event.target.checked,
                            );
                          }}
                        />
                        <span className="category-client-label-name">
                          {clientProject.name}
                        </span>
                        <span className="category-client-label-count">
                          {stats.selectedCount}/{stats.totalCount}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {shiftCategoryBounds?.first && shiftCategoryBounds?.last && (
              <>
                <div className="category-shift-edit-label">New start date</div>
                <div className="category-shift-controls">
                  <input
                    type="text"
                    placeholder="DD/MM/YYYY"
                    value={shiftStartDateDisplay}
                    disabled={!canEdit}
                    onChange={(e) => {
                      setShiftStartDateDisplay(e.target.value);
                    }}
                    onBlur={() => {
                      const frenchDate = shiftStartDateDisplay.trim();
                      if (!frenchDate) {
                        setShiftStartDateInput("");
                        setShiftStartDateDisplay("");
                        return;
                      }

                      const isoDate = parseDateFromFrench(frenchDate);
                      if (isValidIsoDate(isoDate)) {
                        setShiftStartDateInput(isoDate);
                        setShiftStartDateDisplay(parseDateToFrench(isoDate));
                      } else {
                        setShiftStartDateInput("");
                      }
                    }}
                    className="category-shift-date-input"
                  />
                </div>

                <div className="category-shift-actions">
                  <button
                    className="category-shift-cancel-button"
                    onClick={closeShiftModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="category-shift-apply-button"
                    onClick={applyCategoryShift}
                    disabled={!canEdit || !shiftStartDateInput}
                  >
                    Update schedule
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {deleteCategoryTarget && (
        <div
          className="category-shift-modal-overlay"
          onClick={closeDeleteCategoryConfirm}
        >
          <div
            className="category-shift-modal category-move-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="category-shift-modal-header">
              <p className="category-shift-description">Delete category</p>
              <p className="category-shift-help-text">
                You are about to delete{" "}
                <strong>{deleteCategoryTarget.name}</strong>. This action will
                also delete:
              </p>
              <ul className="category-delete-warning-list">
                <li>{deleteCategoryProjectCount} project(s)</li>
                <li>{deleteCategorySubcategoryCount} subcategory(ies)</li>
              </ul>
              <p className="category-shift-help-text">This cannot be undone.</p>
            </div>

            <div className="category-shift-actions">
              <button
                className="category-shift-cancel-button"
                onClick={closeDeleteCategoryConfirm}
              >
                Cancel
              </button>
              <button
                className="category-shift-delete-button"
                onClick={applyDeleteCategory}
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {moveCategoryId !== null && (
        <div className="category-shift-modal-overlay" onClick={closeMoveModal}>
          <div
            className="category-shift-modal category-move-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="category-shift-modal-header">
              <p className="category-shift-description">
                Move category to another parent
              </p>
            </div>

            <div className="category-shift-row">
              <span className="category-shift-current-label">Destination:</span>
              <select
                className="category-move-select"
                value={moveCategoryTargetParentId}
                onChange={(e) => setMoveCategoryTargetParentId(e.target.value)}
              >
                {canMoveToTopLevel && <option value="root">Top level</option>}
                {moveCategoryOptions.map((cat) => (
                  <option key={cat.id} value={String(cat.id)}>
                    {`${"— ".repeat(cat.level ?? 0)}${cat.name}`}
                  </option>
                ))}
              </select>
            </div>

            {onReorderCategory && (
              <div className="category-shift-reorder-row">
                <button
                  className="category-shift-reorder-button"
                  disabled={!canMoveUp}
                  onClick={() => {
                    onReorderCategory(moveCategoryId, "up");
                    closeMoveModal();
                  }}
                  title="Move up among siblings"
                >
                  ↑ Move up
                </button>
                <button
                  className="category-shift-reorder-button"
                  disabled={!canMoveDown}
                  onClick={() => {
                    onReorderCategory(moveCategoryId, "down");
                    closeMoveModal();
                  }}
                  title="Move down among siblings"
                >
                  ↓ Move down
                </button>
              </div>
            )}

            <div className="category-shift-actions">
              <button
                className="category-shift-cancel-button"
                onClick={closeMoveModal}
              >
                Cancel
              </button>
              <button
                className="category-shift-apply-button"
                onClick={applyCategoryMove}
              >
                Move category
              </button>
            </div>
          </div>
        </div>
      )}

      {isCapacityModalOpen && (
        <div
          className="category-shift-modal-overlay"
          onClick={closeCapacityModal}
        >
          <div
            className="category-shift-modal category-move-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="category-shift-modal-header">
              <p className="category-shift-description">
                Configure available workforce
              </p>
              <p className="category-shift-help-text">
                Set available capacity per work category (in person-days per
                month).
              </p>
            </div>

            <div className="capacity-config-grid">
              {allWorkCategories.map((workCategory) => (
                <label key={workCategory} className="capacity-config-row">
                  <span className="capacity-config-label">
                    {WORK_CATEGORY_LABELS[workCategory]}
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    className="capacity-config-input"
                    value={capacityDraft[workCategory]}
                    onChange={(e) =>
                      handleCapacityDraftChange(workCategory, e.target.value)
                    }
                  />
                </label>
              ))}
            </div>

            <div className="category-shift-actions">
              <button
                className="category-shift-cancel-button"
                onClick={closeCapacityModal}
              >
                Cancel
              </button>
              <button
                className="category-shift-cancel-button"
                onClick={resetCapacityConfig}
              >
                Reset defaults
              </button>
              <button
                className="category-shift-apply-button"
                onClick={applyCapacityConfig}
              >
                Save workforce
              </button>
            </div>
          </div>
        </div>
      )}

      {isUserManagementModalOpen && (
        <div
          className="category-shift-modal-overlay"
          onClick={closeUserManagementModal}
        >
          <div
            className="category-shift-modal user-management-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="category-shift-modal-header">
              <p className="category-shift-description">User management</p>
              <p className="category-shift-help-text">
                New Azure users are automatically created with <strong>Default</strong> role.
                Update role to <strong>Viewer</strong> or <strong>Admin</strong> as needed.
              </p>
            </div>

            {userManagementError && (
              <p className="user-management-error">{userManagementError}</p>
            )}

            {isUserManagementLoading ? (
              <p className="category-shift-help-text">Loading connected users…</p>
            ) : (
              <div className="user-management-table-wrapper">
                <table className="user-management-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Provider</th>
                      <th>Last login</th>
                      <th>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managedUsers.map((managedUser) => (
                      <tr key={managedUser.id}>
                        <td>{managedUser.displayName}</td>
                        <td>{managedUser.email}</td>
                        <td>{managedUser.provider}</td>
                        <td>{formatDateTime(managedUser.lastLoginAt)}</td>
                        <td>
                          <select
                            className="user-role-select"
                            value={managedUser.role}
                            disabled={updatingUserId === managedUser.id}
                            onChange={(e) =>
                              handleUserRoleChange(
                                managedUser.id,
                                e.target.value as UserRole,
                              )
                            }
                          >
                            {(["default", "viewer", "admin"] as UserRole[]).map(
                              (role) => (
                                <option key={role} value={role}>
                                  {roleLabelMap[role]}
                                </option>
                              ),
                            )}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="category-shift-actions">
              <button
                className="category-shift-cancel-button"
                onClick={closeUserManagementModal}
              >
                Close
              </button>
              <button
                className="category-shift-cancel-button"
                onClick={loadConnectedUsers}
                disabled={isUserManagementLoading}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}

      {categoryCommentsTarget && (
        <CommentsModal
          isOpen={isCategoryCommentsModalOpen}
          taskId={`category_${categoryCommentsTarget.id}`}
          taskName={categoryCommentsTarget.name}
          canCreateComment={canCreateComments}
          canViewComments={canViewComments}
          onClose={closeCategoryCommentsModal}
          user={authUser}
        />
      )}

      {moveProjectId !== null && (
        <div
          className="category-shift-modal-overlay"
          onClick={closeMoveProjectModal}
        >
          <div
            className="category-shift-modal category-move-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="category-shift-modal-header">
              <p className="category-shift-description">
                Move project{movingProject ? ` "${movingProject.name}"` : ""} to
                another category
              </p>
            </div>

            <div className="category-shift-row">
              <span className="category-shift-current-label">Destination:</span>
              <select
                className="category-move-select"
                value={moveProjectTargetCategoryId}
                onChange={(e) => setMoveProjectTargetCategoryId(e.target.value)}
              >
                {moveProjectOptions.map((cat) => (
                  <option key={cat.id} value={String(cat.id)}>
                    {`${"— ".repeat(cat.level ?? 0)}${cat.name}`}
                  </option>
                ))}
              </select>
            </div>

            {moveProjectOptions.length === 0 && (
              <p className="category-shift-help-text">
                No valid destination found (requires sibling, direct parent,
                parent sibling, or parent's parent category).
              </p>
            )}

            <div className="category-shift-actions">
              <button
                className="category-shift-cancel-button"
                onClick={closeMoveProjectModal}
              >
                Cancel
              </button>
              <button
                className="category-shift-apply-button"
                onClick={applyProjectMove}
                disabled={moveProjectOptions.length === 0}
              >
                Move project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSchedule;
